'use client';

import { useLocale } from 'next-intl';
import { ComponentProps, MouseEvent, useContext } from 'react';
import {
    CustomerNotificationItemContext,
    useCustomerNotificationItem,
} from '@/features/news/customer-notification-item-context';
import { openCustomerNotification } from '@/features/news/open-customer-notification';
import { cn } from '@/shared/lib/cn';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';
import { Media } from '@/shared/ui/media';
import { Media as MediaType } from '@/shared/ui/media/types';
import { ContactNotSeenAvatar } from './contact-not-seen-avatar';
import './contact.scss';

type ContactBlogBoxWrapperProps = ComponentProps<typeof Layout>;

export const ContactBlogBoxWrapper = ({ children, onClick, ...props }: ContactBlogBoxWrapperProps) => {
    const notification = useContext(CustomerNotificationItemContext)?.notification;

    if (!notification) {
        return null;
    }

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
        onClick?.(e);
        void openCustomerNotification(notification, { readPolicy: 'immediate' });
    };

    return (
        <Layout
            element="button"
            type="button"
            className={cn('card', 'contact__box', 'contact__blog-box')}
            data-testid="contact-blog-box"
            data-id={notification.id}
            {...props}
            onClick={handleClick}
        >
            {children}
        </Layout>
    );
};

type ContactBlogBoxNotSeenAvatarProps = ComponentProps<typeof ContactNotSeenAvatar> & {
    isNotSeen?: boolean;
};

export const ContactBlogBoxNotSeenAvatar = ({
    isNotSeen: isNotSeenProp,
    ...props
}: ContactBlogBoxNotSeenAvatarProps) => {
    const { isUnread } = useCustomerNotificationItem();
    const isNotSeen = isNotSeenProp ?? isUnread;

    if (!isNotSeen) {
        return null;
    }
    return <ContactNotSeenAvatar {...props} />;
};

type ContactBlogBoxMediaProps = ComponentProps<typeof Layout> & {
    data?: MediaType;
};

export const ContactBlogBoxMedia = ({ ...props }: ContactBlogBoxMediaProps) => {
    const { notification: data } = useCustomerNotificationItem();
    const getLocalizedValue = useGetLocalizedValue();
    const media = data.media;

    if (!media?.fullUrl) {
        return null;
    }

    return (
        <Layout className="contact__blog-box__media" {...props}>
            <Media data={{ url: media.fullUrl, alt: getLocalizedValue(data.title) || '' }} />
        </Layout>
    );
};

type ContactBlogBoxContentWrapperProps = ComponentProps<typeof Layout>;

export const ContactBlogBoxContentWrapper = ({ ...props }: ContactBlogBoxContentWrapperProps) => {
    return <Layout className="contact__blog-box__content-wrapper" {...props} />;
};

type ContactBlogBoxContentProps = ComponentProps<typeof Layout>;

export const ContactBlogBoxContent = ({ media, ...props }: ContactBlogBoxContentProps) => {
    return <Layout className="contact__blog-box__content" media={media} {...props} />;
};

type ContactBlogBoxTitleProps = ComponentProps<typeof Layout>;

export const ContactBlogBoxTitle = ({ media, ...props }: ContactBlogBoxTitleProps) => {
    const { notification: data } = useCustomerNotificationItem();
    const getLocalizedValue = useGetLocalizedValue();
    const title = getLocalizedValue(data.title);

    if (!title) {
        return null;
    }

    return (
        <Layout
            element="span"
            className="contact__box-title"
            media={media}
            data-testid="contact-blog-box-article-title"
            {...props}
        >
            {title}
        </Layout>
    );
};

type ContactBlogBoxDescriptionProps = ComponentProps<typeof Layout>;

export const ContactBlogBoxDescription = ({ media, ...props }: ContactBlogBoxDescriptionProps) => {
    const { notification: data } = useCustomerNotificationItem();
    const getLocalizedValue = useGetLocalizedValue();
    const content = getLocalizedValue(data.content);

    if (!content) {
        return null;
    }

    return (
        <Layout element="p" media={media} {...props}>
            {content}
        </Layout>
    );
};

type ContactBlogBoxPublishedAtProps = ComponentProps<typeof Layout>;

export const ContactBlogBoxPublishedAt = ({ media, ...props }: ContactBlogBoxPublishedAtProps) => {
    const locale = useLocale();
    const { notification: data } = useCustomerNotificationItem();

    if (!data.publishedAt) {
        return null;
    }

    const formattedDateTime = new Date(data.publishedAt).toLocaleString(locale, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
    });

    return (
        <Layout
            element="span"
            className="contact__blog-box__published-at"
            media={media}
            data-testid="contact-blog-box-article-published-at"
            {...props}
        >
            {formattedDateTime}
        </Layout>
    );
};

type ContactBlogBoxChevronIconProps = ComponentProps<typeof Layout>;

export const ContactBlogBoxChevronIcon = ({ media, ...props }: ContactBlogBoxChevronIconProps) => {
    return (
        <Layout
            media={{
                ...media,
                initial: { width: 24, height: 24, minWidth: 24, minHeight: 24, ...media?.initial },
            }}
            {...props}
        >
            <Icon name="chevron-right" />
        </Layout>
    );
};

export const ContactBlogBox = ContactBlogBoxWrapper;

export const ContactBlogBoxCompound: typeof ContactBlogBoxWrapper & {
    NotSeenAvatar: typeof ContactBlogBoxNotSeenAvatar;
    Media: typeof ContactBlogBoxMedia;
    ContentWrapper: typeof ContactBlogBoxContentWrapper;
    Content: typeof ContactBlogBoxContent;
    Title: typeof ContactBlogBoxTitle;
    Description: typeof ContactBlogBoxDescription;
    PublishedAt: typeof ContactBlogBoxPublishedAt;
    ChevronIcon: typeof ContactBlogBoxChevronIcon;
} = Object.assign(ContactBlogBoxWrapper, {
    NotSeenAvatar: ContactBlogBoxNotSeenAvatar,
    Media: ContactBlogBoxMedia,
    ContentWrapper: ContactBlogBoxContentWrapper,
    Content: ContactBlogBoxContent,
    Title: ContactBlogBoxTitle,
    Description: ContactBlogBoxDescription,
    PublishedAt: ContactBlogBoxPublishedAt,
    ChevronIcon: ContactBlogBoxChevronIcon,
});
